import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import App from '../AppExports'; // Import only from AppExports
import { eventService, invitationService, templateService } from '../services/db';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/dexie-db';
import './InvitationEditorPage.css'; // Reuse existing styles
import { captureFullPageScreenshot } from '../utils/thumbnailGenerator';

console.log("NewInvitationPage is importing App:", typeof App);

// Save notification component - reused from EditorPage
const SaveNotification = ({ isVisible, progress, status }) => {
  if (!isVisible) return null;
  
  return (
    <div className={`save-notification ${status}`}>
      <div className="save-notification-content">
        {status === 'saving' && (
          <>
            <div className="save-spinner"></div>
            <span>Saving invitation...</span>
            {progress < 100 && (
              <div className="save-progress-container">
                <div className="save-progress-bar" style={{ width: `${progress}%` }}></div>
              </div>
            )}
          </>
        )}
        
        {status === 'success' && (
          <>
            <svg className="check-icon" viewBox="0 0 24 24">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
            </svg>
            <span>Invitation saved successfully</span>
          </>
        )}
        
        {status === 'error' && (
          <>
            <svg className="error-icon" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <span>Error saving invitation</span>
          </>
        )}
      </div>
    </div>
  );
};

function NewInvitationPage() {
  // This component is ONLY for new invitations
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // Simple state - no error handling
  const [invitationName, setInvitationName] = useState('New Invitation');
  const [elements, setElements] = useState([]);
  const [loadingTemplate, setLoadingTemplate] = useState(true);
  const [templateId, setTemplateId] = useState(null);
  // Add state to track the ID of the template created from this invitation
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ visible: false, progress: 0, status: 'idle' });
  
  // Update the useEffect to prevent multiple runs
  useEffect(() => {
    const loadTemplateIfNeeded = async () => {
      setLoadingTemplate(true); // Start loading
      console.log("==== LOADING TEMPLATE =====");
      
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const templateIdFromUrl = urlParams.get('templateId');
        
        // Store template ID in state for later use
        setTemplateId(templateIdFromUrl);
        
        if (!templateIdFromUrl) {
          console.log("No template ID - using blank canvas");
          return;
        }
        
        // Simplified template loading
        const template = await templateService.getById(templateIdFromUrl);
        
        if (template && template.elements && Array.isArray(template.elements)) {
          console.log(`Found template "${template.name}" with ${template.elements.length} elements`);
          
          // Set invitation name
          setInvitationName(template.name);
          
          // Create stable copy of elements with new IDs
          const stableElements = template.elements.map(el => ({
            ...el,
            id: `${el.id || 'el'}-copy`  // More stable IDs without Date.now()
          }));
          
          // Set elements once
          setElements(stableElements);
        }
      } catch (err) {
        console.error("Template loading error:", err);
      } finally {
        setLoadingTemplate(false); // End loading
      }
    };
    
    loadTemplateIfNeeded();
  }, []); // Empty dependency array prevents re-runs
  
  // Add this code right before loadTemplateIfNeeded
  useEffect(() => {
    // Direct database debugging for templates
    async function debugTemplatesTable() {
      try {
        const allTemplates = await db.templates.toArray();
        console.log("==== DIRECT DB QUERY: ALL TEMPLATES =====");
        console.log(`Found ${allTemplates.length} templates in database`);
        allTemplates.forEach((template, idx) => {
          console.log(`Template ${idx}:`);
          console.log(`- ID: ${template.id}`);
          console.log(`- Name: ${template.name}`);
          console.log(`- Has elements: ${!!template.elements}`);
          console.log(`- Elements count: ${template.elements?.length || 0}`);
        });
      } catch (err) {
        console.error("Error querying templates table:", err);
      }
    }
    
    debugTemplatesTable();
  }, []);
  
  const handleSaveInvitation = async (publish = false) => {
    try {
      // Show saving notification
      setSaveStatus({ visible: true, progress: 10, status: 'saving' });
      
      // Try to get event info
      let eventInfo;
      try {
        eventInfo = await eventService.getById(eventId);
        console.log("Loaded event:", eventInfo);
      } catch (err) {
        console.log("Could not load event, using default name");
      }
      
      // Create invitation with minimal data
      const invitation = {
        eventId: String(eventId),
        name: invitationName.trim() || 'New Invitation',
        elements: elements,
        published: publish
      };
      
      console.log("Saving new invitation:", invitation);
      setSaveStatus({ visible: true, progress: 25, status: 'saving' });
      
      // Enable preview mode to generate thumbnail
      setIsPreviewMode(true);
      
      // Capture screenshot for thumbnail
      console.log("📥 [NewInvitationPage] Attempting to capture canvas screenshot for template");
      let screenshot = null;
      
      try {
        // Try to capture the screenshot
        screenshot = await captureFullPageScreenshot();
        setSaveStatus({ visible: true, progress: 40, status: 'saving' });
        
        if (screenshot) {
          console.log("✅ [NewInvitationPage] Canvas screenshot captured successfully, length:", screenshot.length);
        } else {
          console.error("❌ [NewInvitationPage] Failed to capture canvas screenshot");
        }
      } catch (screenshotError) {
        console.error("❌ [NewInvitationPage] Error capturing canvas screenshot:", screenshotError);
      }
      
      // Save to database
      setSaveStatus({ visible: true, progress: 60, status: 'saving' });
      const savedInvitation = await invitationService.create(invitation);
      console.log("Saved invitation:", savedInvitation);
      
      // Template handling - create on first save, update on subsequent saves
      console.log("Handling template...");
      try {
        // Prepare template data (same as invitation but without eventId)
        const templateData = {
          name: invitation.name + " (Template)",
          elements: elements, // Use the elements directly
          userId: currentUser.id, // Required for template
          description: `Template created from invitation "${invitation.name}"`,
          createdFromEventId: String(eventId), // For reference only
          updatedAt: new Date().toISOString()
        };
        
        // Add the screenshot as thumbnail if available
        if (screenshot) {
          console.log("✅ [NewInvitationPage] Adding screenshot as template thumbnail");
          templateData.thumbnail = screenshot;
        }
        
        setSaveStatus({ visible: true, progress: 80, status: 'saving' });
        let savedTemplate;
        
        // Check if we already have a saved template ID
        if (savedTemplateId) {
          // First check if template still exists
          try {
            console.log("Checking if template exists with ID:", savedTemplateId);
            const existingTemplate = await templateService.getById(savedTemplateId);
            
            if (existingTemplate) {
              // Update existing template
              console.log("Updating existing template with ID:", savedTemplateId);
              savedTemplate = await templateService.update(savedTemplateId, {
                ...templateData,
                id: savedTemplateId
              });
              console.log("Template updated:", savedTemplate.id);
            } else {
              // Template not found, create a new one
              console.log("Saved template ID doesn't exist, creating new template");
              templateData.createdAt = new Date().toISOString();
              savedTemplate = await templateService.create(templateData);
              console.log("New template created with ID:", savedTemplate.id);
              setSavedTemplateId(savedTemplate.id);
            }
          } catch (templateCheckError) {
            console.error("Error checking template:", templateCheckError);
            
            // Create a new template as fallback
            console.log("Creating new template after failed ID check");
            templateData.createdAt = new Date().toISOString();
            savedTemplate = await templateService.create(templateData);
            console.log("New template created with ID:", savedTemplate.id);
            setSavedTemplateId(savedTemplate.id);
          }
        } else {
          // Create new template only on first save
          console.log("Creating new template for first save");
          templateData.createdAt = new Date().toISOString();
          savedTemplate = await templateService.create(templateData);
          console.log("New template created with ID:", savedTemplate.id);
          // Store the template ID for future updates
          setSavedTemplateId(savedTemplate.id);
        }
      } catch (templateErr) {
        // Don't fail the whole operation if template saving fails
        console.error("Error saving template:", templateErr);
        // We'll continue with the success flow without showing an error
      }
      
      // Disable preview mode
      setIsPreviewMode(false);
      
      // Update status to success
      setSaveStatus({ visible: true, progress: 100, status: 'success' });
      
      if (!publish) {
        // Navigate to edit page with new ID and include the template ID as a query parameter
        // Make sure we only include the template ID if it's not null or empty
        const templateParam = savedTemplateId ? `?fromTemplateId=${savedTemplateId}` : '';
        
        // Hide the success notification after a delay
        setTimeout(() => {
          setSaveStatus({ visible: false, progress: 0, status: 'idle' });
          navigate(`/events/${eventId}/invitation/${savedInvitation.id}/edit${templateParam}`, { replace: true });
        }, 2000);
      } else {
        // Hide the success notification after a delay
        setTimeout(() => {
          setSaveStatus({ visible: false, progress: 0, status: 'idle' });
          // Navigate back to event page
          navigate(`/events/${eventId}`);
        }, 2000);
      }
    } catch (err) {
      console.error("Failed to save:", err);
      
      // Show error notification
      setSaveStatus({ visible: true, progress: 100, status: 'error' });
      
      // Disable preview mode
      setIsPreviewMode(false);
      
      // Hide the error notification after a delay
      setTimeout(() => {
        setSaveStatus({ visible: false, progress: 0, status: 'idle' });
      }, 3000);
    }
  };
  
  // Simple clean render with no conditionals
  return (
    <div className="invitation-editor">
      <div className="editor-header">
        <div className="editor-controls">
          <button className="back-btn" onClick={() => navigate(`/events/${eventId}`)}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 8H1M1 8L8 1M1 8L8 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Event
          </button>
          <input
            type="text"
            className="invitation-name-input"
            placeholder="Invitation Name"
            value={invitationName}
            onChange={(e) => setInvitationName(e.target.value)}
          />
        </div>
        <div className="editor-actions">
          <button 
            className="save-draft-btn" 
            onClick={() => handleSaveInvitation(false)}
            disabled={saveStatus.status === 'saving'}
          >
            {saveStatus.status === 'saving' ? 'Saving...' : 'Save Draft'}
          </button>
          <button 
            className="publish-btn" 
            onClick={() => handleSaveInvitation(true)}
            disabled={saveStatus.status === 'saving'}
          >
            {saveStatus.status === 'saving' ? 'Publishing...' : 'Publish Invitation'}
          </button>
        </div>
      </div>
      
      <SaveNotification 
        isVisible={saveStatus.visible}
        progress={saveStatus.progress}
        status={saveStatus.status}
      />
      
      <div className="editor-container">
        <App 
          initialElements={elements}
          onElementsChange={setElements}
          eventId={eventId}
          invitationId="new-invitation"
          key={`new-invitation-${templateId || 'blank'}`}
          isPreviewMode={isPreviewMode}
        />
      </div>
    </div>
  );
}

export default NewInvitationPage; 